import React, { useState, useEffect, useMemo } from 'react';
import { Layers, Activity, AlertTriangle, CornerUpLeft, Info, ChevronRight, X, Download } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import {
    FUNNEL_STAGES, isEffectivelyAtOrAfter, computeFunnel,
    fmtNum, fmtPct, fmtKwh, type FunnelRow,
} from '../../../src/lib/funnelUtils';
import { buildSheet, downloadWorkbook, fileStamp } from '../../../src/lib/excelExport';

export interface FunnelNegRow extends FunnelRow {
    id: number;
    titulo?: string | null;
    created_at: string;
    consumo_medio_kwh: number | null;
    stage_changed_at?: string | null;
}

interface FunilDinamicoProps {
    /** Linhas já filtradas por responsável (todas, não limitadas ao período). */
    rows: FunnelNegRow[];
    dateStart: string;
    dateEnd: string;
    /** Abre a negociação no detalhe (fecha o dashboard). */
    onOpenNegociacao?: (id: number) => void;
}

type Mode = 'snapshot' | 'fluxo';

// Mapeia o status atual para a etapa "estoque" (Snapshot) — bucket exclusivo.
const statusToStageKey = (status: string): string | null => {
    switch (status) {
        case 'em_andamento': return 'prospeccao';
        case 'simulacao_realizada': return 'simulacao';
        case 'termo_enviado': return 'termo_enviado';
        case 'termo_assinado':
        case 'termo_analise': return 'termo_assinado';
        case 'termo_aprovado':
        case 'ganho': return 'termo_aprovado';
        default: return null; // perdido / em_pausa / termo_reprovado ficam fora das etapas ativas
    }
};

// Rank da etapa do funil por nome de etapa (pipeline) OU status — usado p/ detectar retorno.
const STAGE_RANK_KEYWORDS: Array<[RegExp, number]> = [
    [/prospec|lead/i, 0],
    [/qualific|simula/i, 1],
    [/enviad/i, 2],
    [/assinad|ades[ãa]o|an[áa]lise/i, 3],
    [/aprovad|ganho/i, 4],
];
const rankFromText = (text?: string | null): number | null => {
    if (!text) return null;
    for (const [re, rank] of STAGE_RANK_KEYWORDS) if (re.test(text)) return rank;
    return null;
};

const daysBetween = (a: Date, b: Date) => Math.max(0, (b.getTime() - a.getTime()) / 86_400_000);

// Larguras decrescentes do trapézio centralizado (em %).
const TRAPEZOID_WIDTHS = [100, 84, 68, 54, 42];

const FunilDinamico: React.FC<FunilDinamicoProps> = ({ rows, dateStart, dateEnd, onOpenNegociacao }) => {
    const [mode, setMode] = useState<Mode>('snapshot');
    const [selectedStage, setSelectedStage] = useState<string | null>(null);
    const [retornos, setRetornos] = useState<number>(0);

    const start = useMemo(() => new Date(dateStart + 'T00:00:00'), [dateStart]);
    const end = useMemo(() => new Date(dateEnd + 'T23:59:59'), [dateEnd]);

    // Coorte do período: negociações criadas dentro de [start, end].
    const cohort = useMemo(
        () => rows.filter(r => { const d = new Date(r.created_at); return d >= start && d <= end; }),
        [rows, start, end],
    );

    // ── Retorno (Termo Enviado → Simulação) via eventos estruturados de etapa ──
    useEffect(() => {
        let cancelled = false;
        const fetchRetornos = async () => {
            const ids = cohort.map(r => r.id);
            if (ids.length === 0) { setRetornos(0); return; }
            const { data } = await supabase
                .from('negociacao_stage_events')
                .select('negociacao_id, from_status, to_status, from_stage, to_stage, changed_at')
                .in('negociacao_id', ids)
                .gte('changed_at', start.toISOString())
                .lte('changed_at', end.toISOString());
            if (cancelled) return;
            const count = (data || []).filter((e: any) => {
                const fromRank = rankFromText(e.from_status) ?? rankFromText(e.from_stage);
                const toRank = rankFromText(e.to_status) ?? rankFromText(e.to_stage);
                // Regressão: caiu de "Termo Enviado" (rank 2) ou além de volta p/ "Simulação" (rank 1) ou antes.
                return fromRank !== null && toRank !== null && fromRank >= 2 && toRank <= 1;
            }).length;
            setRetornos(count);
        };
        fetchRetornos();
        return () => { cancelled = true; };
    }, [cohort, start, end]);

    // ── Métricas por etapa (Snapshot = estoque exclusivo · Fluxo = alcance cumulativo) ──
    const data = useMemo(() => {
        const total = cohort.length;
        const now = new Date();

        // Leads atualmente parados em cada etapa (bucket exclusivo) — base do Snapshot e do aging.
        const bucket: Record<string, FunnelNegRow[]> = {};
        FUNNEL_STAGES.forEach(s => { bucket[s.key] = []; });
        cohort.forEach(r => {
            const key = statusToStageKey(r.status);
            if (key && bucket[key]) bucket[key].push(r);
        });

        const { stepRates } = computeFunnel(cohort);
        const minRate = stepRates.length
            ? Math.min(...stepRates.map(s => s.rate))
            : 0;

        const stages = FUNNEL_STAGES.map((s, idx) => {
            // Snapshot: só os que estão parados nesta etapa agora.
            // Fluxo: todos os da coorte que alcançaram (passaram por) esta etapa.
            const list = mode === 'snapshot'
                ? bucket[s.key]
                : cohort.filter(r => s.target === null ? true : isEffectivelyAtOrAfter(r, s.target));
            const count = list.length;
            const kwh = list.reduce((a, r) => a + (r.consumo_medio_kwh || 0), 0);
            const pct = total > 0 ? (count / total) * 100 : 0;

            // Aging = tempo médio que os leads ATUALMENTE nesta etapa estão parados (now - stage_changed_at).
            const parked = bucket[s.key];
            const aging = parked.length
                ? parked.reduce((a, r) => a + daysBetween(new Date(r.stage_changed_at || r.created_at), now), 0) / parked.length
                : 0;

            return { ...s, count, kwh, pct, aging, list, width: TRAPEZOID_WIDTHS[idx] ?? 40 };
        });

        return { stages, stepRates, minRate, total };
    }, [cohort, mode]);

    const selected = selectedStage ? data.stages.find(s => s.key === selectedStage) : null;

    const exportFunil = () => {
        const modoTag = mode === 'snapshot' ? 'Snapshot' : 'Fluxo';
        const wsEtapas = buildSheet(
            ['Etapa', 'Leads', 'kWh', '% do total', 'Aging (dias)'],
            data.stages.map(s => [s.label, s.count, Math.round(s.kwh), Number(s.pct.toFixed(1)), Number(s.aging.toFixed(1))]),
            ['txt', 'num', 'num', 'pct', 'num'],
        );
        const wsConv = buildSheet(
            ['De', 'Para', 'Conversão %'],
            data.stepRates.map(r => [r.from, r.to, Number(r.rate.toFixed(1))]),
            ['txt', 'txt', 'pct'],
        );
        downloadWorkbook([
            { name: `Funil ${modoTag}`, ws: wsEtapas },
            { name: 'Conversão', ws: wsConv },
        ], `Funil_${modoTag}_${dateStart}_a_${dateEnd}_${fileStamp()}.xlsx`);
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 md:p-6">
            {/* Header + toggle de modo */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <div>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <Layers size={18} className="text-primary" />
                        Funil Dinâmico
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {mode === 'snapshot'
                            ? 'Estoque atual — leads parados em cada etapa (coorte do período)'
                            : 'Vazão — leads do período que passaram por cada etapa'}
                    </p>
                </div>

                <div className="flex items-center gap-2 self-start">
                    <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                        <button
                            onClick={() => setMode('snapshot')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === 'snapshot' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            <Layers size={13} /> Snapshot
                        </button>
                        <button
                            onClick={() => setMode('fluxo')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === 'fluxo' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            <Activity size={13} /> Fluxo
                            <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" title="Vazão estimada a partir da coorte de leads criados no período.">estim.</span>
                        </button>
                    </div>
                    <button
                        onClick={exportFunil}
                        disabled={data.total === 0}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 dark:hover:bg-emerald-900/20 disabled:opacity-40 disabled:cursor-default transition-all"
                        title="Exportar funil para Excel"
                    >
                        <Download size={13} /> Excel
                    </button>
                </div>
            </div>

            {data.total === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                    <Layers size={36} className="mb-2 opacity-30" />
                    <p className="text-sm">Nenhum lead criado no período selecionado.</p>
                </div>
            ) : (
                <div className="max-w-2xl mx-auto">
                    {data.stages.map((s, idx) => {
                        const conn = idx > 0 ? data.stepRates[idx - 1] : null; // conversão da etapa anterior → esta
                        const isGargalo = conn ? conn.rate === data.minRate && data.minRate > 0 : false;
                        const showRetorno = s.key === 'simulacao'; // conector Prospecção→Simulação carrega o ↩ retorno
                        const isSelected = selectedStage === s.key;
                        return (
                            <React.Fragment key={s.key}>
                                {/* Conector entre etapas: conversão · aging · gargalo · retorno */}
                                {conn && (
                                    <div className="flex flex-col items-center py-1.5 select-none">
                                        <div className="flex items-center gap-2 text-[11px]">
                                            <span className="font-bold text-gray-500 dark:text-gray-400">▼ {fmtPct(conn.rate)}</span>
                                            {isGargalo && (
                                                <span className="flex items-center gap-1 font-bold text-amber-600 dark:text-amber-400" title="Maior gargalo do período">
                                                    <AlertTriangle size={11} /> gargalo
                                                </span>
                                            )}
                                            {showRetorno && (
                                                <span
                                                    className="flex items-center gap-0.5 font-bold text-rose-500 dark:text-rose-400"
                                                    title={retornos > 0 ? 'Leads que voltaram de Termo Enviado para Simulação no período' : 'Sem retornos registrados — passa a contar à medida que houver histórico de etapas'}
                                                >
                                                    <CornerUpLeft size={11} /> {retornos}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Segmento do trapézio (clicável → drill-down) */}
                                <button
                                    onClick={() => setSelectedStage(isSelected ? null : s.key)}
                                    className="block mx-auto transition-all hover:scale-[1.01] focus:outline-none"
                                    style={{ width: `${s.width}%` }}
                                >
                                    <div
                                        className={`rounded-xl px-4 py-3 border-2 text-left ${isSelected ? 'ring-2 ring-offset-1 dark:ring-offset-gray-800' : ''}`}
                                        style={{
                                            background: `${s.color}1a`,
                                            borderColor: `${s.color}55`,
                                            ...(isSelected ? { boxShadow: `0 0 0 2px ${s.color}` } : {}),
                                        }}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wide truncate" style={{ color: s.color }}>
                                                {s.label}
                                            </span>
                                            <span className="text-[10px] font-bold font-mono px-1.5 py-0.5 rounded shrink-0" style={{ background: `${s.color}22`, color: s.color }}>
                                                {fmtPct(s.pct)}
                                            </span>
                                        </div>
                                        <div className="flex items-baseline justify-between gap-2 mt-1">
                                            <span className="text-2xl font-extrabold font-mono leading-none" style={{ color: s.color }}>
                                                {fmtNum(s.count)}
                                            </span>
                                            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">{fmtKwh(s.kwh)}</span>
                                        </div>
                                        <div className="flex items-center justify-between mt-1.5">
                                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                                {s.aging > 0 ? `parado há ~${s.aging.toFixed(0)}d em média` : '—'}
                                            </span>
                                            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                                                ver leads <ChevronRight size={11} />
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            </React.Fragment>
                        );
                    })}

                    {/* Drill-down: lista de leads da etapa selecionada */}
                    {selected && (
                        <div className="mt-5 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                                    {selected.label} — {fmtNum(selected.count)} {selected.count === 1 ? 'lead' : 'leads'}
                                </span>
                                <button onClick={() => setSelectedStage(null)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                                    <X size={15} />
                                </button>
                            </div>
                            <div className="max-h-72 overflow-y-auto custom-scrollbar divide-y divide-gray-100 dark:divide-gray-700/60">
                                {selected.list.length === 0 ? (
                                    <p className="px-4 py-6 text-center text-xs text-gray-400">Nenhum lead nesta etapa.</p>
                                ) : (
                                    selected.list
                                        .slice()
                                        .sort((a, b) => (b.consumo_medio_kwh || 0) - (a.consumo_medio_kwh || 0))
                                        .map(lead => (
                                            <button
                                                key={lead.id}
                                                onClick={() => onOpenNegociacao?.(lead.id)}
                                                disabled={!onOpenNegociacao}
                                                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors disabled:cursor-default"
                                            >
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                                                    {lead.titulo || `Negociação #${lead.id}`}
                                                </span>
                                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400 shrink-0">
                                                    {fmtKwh(lead.consumo_medio_kwh || 0)}
                                                </span>
                                            </button>
                                        ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Nota de método */}
                    <p className="flex items-start gap-1.5 text-[10px] text-gray-400 dark:text-gray-500 mt-4 leading-relaxed">
                        <Info size={12} className="shrink-0 mt-0.5" />
                        Coorte = leads criados no período. Aging e retorno são estimados a partir do histórico
                        disponível e ganham precisão conforme as transições de etapa são registradas.
                    </p>
                </div>
            )}
        </div>
    );
};

export default FunilDinamico;
